import { paths } from "@/routes/paths";
import { useState, useEffect } from "react";
import { useRouter, usePathname } from "@/routes/hooks";
import { useAuthContext } from "@/hooks/use-auth-context";
import { SplashScreen } from "@/components/loading-screen";

type Props = {
  children: React.ReactNode;
};

export function OnboardingGuard({ children }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, loading } = useAuthContext();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (loading) return;

    const checkOnboarding = () => {
      // Skip onboarding check for these paths
      const skippedPaths = [
        paths.auth.login,
        paths.auth.signup,
        paths.auth.verifyEmail,
        paths.auth.resetPassword,
        paths.auth.updatePassword,
        paths.auth.onboarding,
        "/",
        "/blogs",
        "/features",
        "/pricing",
        "/contact",
        "/terms-of-service",
        "/privacy",
      ];

      // Don't check onboarding for public paths
      if (skippedPaths.some((path) => pathname.startsWith(path)) || !user) {
        setIsChecking(false);
        return;
      }

      // Check if onboarding is completed
      const onboardingCompleted =
        user.user_metadata?.onboarding_completed === true;

      // If onboarding is not completed and we're not already on the onboarding page, redirect
      if (!onboardingCompleted && pathname !== paths.auth.onboarding) {
        router.replace(paths.auth.onboarding);
      } else {
        setIsChecking(false);
      }
    };

    checkOnboarding();
  }, [loading, pathname, router, user]);

  if (loading || isChecking) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}

export default OnboardingGuard;
